import { useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { EmptyState } from '@/components/common/empty-state'
import { ErrorDisplay } from '@/components/common/error-display'
import { usePositionQuestions } from '@/features/positions'
import { Skeleton } from '@/components/ui/skeleton'
import { AuthGuard, useCurrentOrganizationId, useOrganizationData } from '@/features/auth'

export const Route = createFileRoute('/(dashboard)/positions/$id/questions')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      title: (search.title as string) || undefined,
      level: (search.level as string) || undefined,
      role: (search.role as string) || undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    // Try to get position data from search params
    const positionTitle = search.title
    const positionLevel = search.level
    const positionRole = search.role

    return {
      breadcrumbs: [
        {
          title: 'Positions',
          // Include role and level in Positions link if available
          href:
            positionRole && positionLevel
              ? `/positions/?role=${encodeURIComponent(positionRole)}&level=${encodeURIComponent(positionLevel)}`
              : '/positions/',
        },
        {
          title:
            positionTitle && positionLevel
              ? `${positionTitle} (${positionLevel})`
              : params.id,
          // If we have role and level, make it a link back to positions with accordion open
          href:
            positionRole && positionLevel
              ? `/positions/?role=${encodeURIComponent(positionRole)}&level=${encodeURIComponent(positionLevel)}`
              : undefined,
        },
        { title: 'Questions' },
      ],
    }
  },
})

function RouteComponent() {
  const { id: positionId } = Route.useParams()
  const { groupedQuestions, isLoading, isError, error } =
    usePositionQuestions(positionId)
  const currentOrgId = useCurrentOrganizationId()
  const organizations = useOrganizationData()

  // Get organization name for placeholder replacement
  const organizationName = useMemo(() => {
    const org = organizations.find(
      (o) => o.id === currentOrgId,
    )
    return org?.name || 'the organization'
  }, [organizations, currentOrgId])

  // Helper function to replace placeholders in question text
  const replacePlaceholders = (text: string | null | undefined): string => {
    if (!text) return ''
    return text
      .replace(/{first_name}/g, 'The employee')
      .replace(/{organization_name}/g, organizationName)
  }

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: currentOrgId || '',
        role: 'admin',
      }}
    >
      {isLoading ? (
        <div className="space-y-6">
          {/* Header Section */}
          <div className="p-6 bg-muted/30 rounded-lg">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>

          <div className="space-y-6">
            {Array.from({ length: 3 }).map((__, i) => (
              <div key={i} className="p-6 bg-muted/30 rounded-lg space-y-6">
                {/* Category Title Skeleton */}
                <Skeleton className="h-7 w-32" />

                {/* Subcategories and Questions Skeleton */}
                <div className="space-y-8">
                  {Array.from({ length: 2 }).map((___, j) => (
                    <div key={j} className="space-y-4">
                      {/* Subcategory Header Skeleton */}
                      <Skeleton className="h-6 w-48 mb-3" />
                      <Skeleton className="h-px w-full mb-4" />

                      {/* Questions Skeleton - Two column grid for manager/employee views */}
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((____, k) => (
                          <div
                            key={k}
                            className="grid grid-cols-1 md:grid-cols-2 gap-3"
                          >
                            <div className="p-4 rounded-md bg-card/50 space-y-2">
                              <Skeleton className="h-4 w-28" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-5/6" />
                            </div>
                            <div className="p-4 rounded-md bg-card/50 space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-4/5" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isError ? (
        <div className="p-6 bg-muted/30 rounded-lg">
          <ErrorDisplay error={error} title="Error loading questions" />
        </div>
      ) : Object.keys(groupedQuestions).length === 0 ? (
        <div className="p-6 bg-muted/30 rounded-lg">
          <EmptyState title="No questions found for this position." />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Header Section */}
          <div className="p-6 bg-muted/30 rounded-lg">
            <h1 className="text-2xl font-semibold text-foreground">
              Position Questions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Review the assessment questions for this position
            </p>
          </div>

          <div className="space-y-6">
            {Object.entries(groupedQuestions).map(([name, subGroups]) => (
              <div key={name} className="p-6 bg-muted/30 rounded-lg space-y-6">
                {/* Category Title */}
                <h2 className="font-semibold text-lg">{name}</h2>

                {/* Subcategories and Questions */}
                <div className="space-y-8">
                  {Object.entries(subGroups).map(([subName, questions]) => (
                    <div key={subName} className="space-y-4">
                      {/* Subcategory Header */}
                      <div className="pb-3 border-b-2">
                        <h4 className="font-semibold text-base text-foreground">
                          {subName}
                        </h4>
                      </div>

                      {/* Questions List */}
                      <div className="space-y-3">
                        {questions.map((question) => (
                          <div key={question.id}>
                            {question.visibleText.showBoth ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                                <div className="p-4 rounded-md bg-card/50 hover:bg-card/80 transition-colors h-full flex flex-col">
                                  <p className="text-sm font-medium text-primary mb-2">
                                    Manager View:
                                  </p>
                                  <p className="text-sm leading-relaxed text-foreground">
                                    {replacePlaceholders(
                                      question.visibleText.managerText,
                                    )}
                                  </p>
                                </div>
                                <div className="p-4 rounded-md bg-card/50 hover:bg-card/80 transition-colors h-full flex flex-col">
                                  <p className="text-sm font-medium text-secondary mb-2">
                                    Direct Report View:
                                  </p>
                                  <p className="text-sm leading-relaxed text-foreground">
                                    {replacePlaceholders(
                                      question.visibleText.directReportText,
                                    )}
                                  </p>
                                </div>
                              </div>
                            ) : question.visibleText.managerText ? (
                              <div className="p-4 rounded-md bg-card/50 hover:bg-card/80 transition-colors">
                                <p className="text-sm leading-relaxed text-foreground">
                                  {replacePlaceholders(
                                    question.visibleText.managerText,
                                  )}
                                </p>
                              </div>
                            ) : (
                              <div className="p-4 rounded-md bg-card/50 hover:bg-card/80 transition-colors">
                                <p className="text-sm leading-relaxed text-foreground">
                                  {replacePlaceholders(
                                    question.visibleText.directReportText,
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AuthGuard>
  )
}
